<template>
    <div class="main-problems-report">
        <div class="card shadow-sm border-0">

            <HeaderFilter
                title="Principais itens de Checklist selecionados"
                @apply-filters="loadData"
            />

            <div class="card-body">
                <!-- Loading -->
                <div
                    v-if="loadingData"
                    class="text-center py-4"
                >
                    <i class="fas fa-spinner fa-spin fa-2x text-danger"></i>
                    <p class="mt-2">Analisando dados...</p>
                </div>

                <!-- Conteúdo Principal -->
                <div v-else>
                    <!-- Alerta de Status -->
                    <div class="status-alert mb-4">
                        <div
                            class="alert"
                            :class="getStatusAlertClass()"
                        >
                            <div class="alert-content">
                                <div class="alert-icon">
                                    <i :class="getStatusIcon()"></i>
                                </div>
                                <div class="alert-text">
                                    <strong>Resumo da Análise:</strong> {{ getStatusMessage() }}
                                    <div class="alert-details">
                                        {{ getTotalItems() }} itens analisados • {{ getTotalQuantity() }} ocorrências
                                        registradas
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Estatísticas Principais -->
                    <div
                        class="critical-stats mb-4"
                        v-if="hasData"
                    >
                        <div class="row">
                            <div class="col-md-3">
                                <div class="critical-card primary-stat">
                                    <div class="critical-icon">
                                        <i class="fas fa-list-alt"></i>
                                    </div>
                                    <div class="critical-content">
                                        <div class="critical-value">{{ getTotalItems() }}</div>
                                        <div class="critical-label">Itens Analisados</div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="critical-card quantity-stat">
                                    <div class="critical-icon">
                                        <i class="fas fa-calculator"></i>
                                    </div>
                                    <div class="critical-content">
                                        <div class="critical-value">{{ getTotalQuantity() }}</div>
                                        <div class="critical-label">Total de Ocorrências</div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="critical-card average-stat">
                                    <div class="critical-icon">
                                        <i class="fas fa-chart-line"></i>
                                    </div>
                                    <div class="critical-content">
                                        <div class="critical-value">{{ getAverageQuantity() }}</div>
                                        <div class="critical-label">Média por Item</div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="critical-card leader-stat">
                                    <div class="critical-icon">
                                        <i class="fas fa-trophy"></i>
                                    </div>
                                    <div class="critical-content">
                                        <div class="critical-value">{{ getLeaderQuantity() }}</div>
                                        <div class="critical-label">Maior Quantidade</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Top Itens -->
                    <div
                        class="top-problems mb-4"
                        v-if="hasData"
                    >
                        <h6 class="section-title">
                            <i class="fas fa-ranking-star mr-2"></i>
                            Ranking dos Itens por Quantidade
                        </h6>
                        <div class="problems-list">
                            <div
                                v-for="(item, index) in getTopItems()"
                                :key="index"
                                class="problem-item"
                                :class="getItemRankClass(item, index)"
                            >
                                <div class="problem-rank">{{ index + 1 }}</div>
                                <div class="problem-content">
                                    <div class="problem-header">
                                        <div class="problem-title">{{ item.label }}</div>
                                        <div class="problem-severity">
                                            <span
                                                class="severity-badge"
                                                :class="getRankClass(index)"
                                            >
                                                {{ getRankLabel(index) }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="problem-stats">
                                        <div class="problem-count">
                                            <i class="fas fa-hashtag mr-1"></i>
                                            {{ item.quantity }} unidades
                                        </div>
                                        <div class="problem-percentage">
                                            {{ getPercentage(item.quantity) }}% do total
                                        </div>
                                    </div>
                                </div>
                                <div class="problem-indicator">
                                    <div class="indicator-bar">
                                        <div
                                            class="indicator-fill"
                                            :style="`width: ${getPercentage(item.quantity)}%`"
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Gráfico -->
                    <div
                        class="chart-container mb-4"
                        v-if="hasData"
                    >
                        <div class="chart-header">
                            <h6 class="chart-title">
                                <i class="fas fa-chart-bar mr-2"></i>
                                Distribuição de Quantidades por Item
                            </h6>
                        </div>
                        <ChartBar
                            v-if="renderChart && chartData.labels"
                            :chart-data="chartData"
                            :options="chartOptions"
                            :height="300"
                        />
                    </div>

                    <!-- Tabela Detalhada -->
                    <div
                        class="problems-table"
                        v-if="hasData"
                    >
                        <div class="table-responsive">
                            <table class="table table-hover">
                                <thead class="bg-light">
                                <tr>
                                    <th>Item</th>
                                    <th class="text-center">Quantidade</th>
                                    <th class="text-center">Participação (%)</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="(item, index) in getTableItems()"
                                    :key="index"
                                    :class="getTableRowClass(index)"
                                >
                                    <td>
                                        <div class="problem-info">
                                            <div class="problem-name">
                                                <i
                                                    :class="getItemIcon(index)"
                                                    class="mr-2"
                                                ></i>
                                                {{ item.label }}
                                            </div>
                                            <div class="problem-meta">
                                                <span
                                                    class="impact-badge"
                                                    :class="getItemClass(index)"
                                                >
                                                    {{ getItemStatus(index) }}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-center">
                                        <span
                                            class="badge"
                                            :class="getQuantityBadgeClass(index)"
                                        >{{ item.quantity }}
                                        </span>
                                    </td>
                                    <td class="text-center">
                                        <strong class="text-primary">{{ getPercentage(item.quantity) }}%</strong>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Estado Vazio -->
                    <div
                        v-if="!hasData"
                        class="empty-state"
                    >
                        <div class="empty-icon">
                            <i class="fas fa-search fa-3x text-muted"></i>
                        </div>
                        <div class="empty-title">Nenhum Dado Encontrado</div>
                        <div class="empty-subtitle">
                            Não foram encontrados dados para o período selecionado.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ChartBar from "@/components/Utils/ChartBar.js";
import {getMessage} from "../../composables/message";
import HeaderFilter from "./HeaderFilter.vue";

export default {
    name: "MainProblemsReport", // nome do componente

    components: {
        HeaderFilter,
        ChartBar, // componente de gráfico de barras
    },

    data() {
        return {
            loadingData: false, // controla carregamento dos dados
            renderChart: false, // controla renderização do gráfico

            // dashData - dados do dashboard
            dashData: {
                graphics: {
                    problemsperiodPerUser: {
                        labels: [], // períodos
                        datasets: [] // dados dos problemas
                    }
                }
            },

            // chartOptions - opções de configuração do gráfico
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top',
                        labels: {
                            usePointStyle: true,
                            padding: 20
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        borderWidth: 1,
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: 'rgba(0, 0, 0, 0.1)'
                        },
                        ticks: {
                            stepSize: 1
                        }
                    },
                    x: {
                        grid: {
                            display: false
                        }
                    }
                }
            },
        };
    },

    computed: {
        /**
         * Verifica se há dados / Check if there's data
         */
        hasData() {
            return this.dashData.graphics.problemsperiodPerUser.datasets.length > 0 &&
                this.dashData.graphics.problemsperiodPerUser.labels.length > 0;
        },

        /**
         * Dados do gráfico / Chart data
         */
        chartData() {
            return this.dashData.graphics.problemsperiodPerUser;
        },

        /**
         * Lista de itens com suas quantidades / List of items with quantities
         */
        itemsList() {
            if (!this.hasData) return [];

            const labels = this.dashData.graphics.problemsperiodPerUser.labels;
            const dataset = this.dashData.graphics.problemsperiodPerUser.datasets[0];

            if (!dataset) return [];

            return labels.map((label, index) => ({
                label: label,
                quantity: dataset.data[index] || 0
            }));
        },

        /**
         * Total de itens / Total items
         */
        totalItems() {
            return this.itemsList.length;
        },

        /**
         * Total de quantidades / Total quantities
         */
        totalQuantity() {
            return this.itemsList.reduce((sum, item) => sum + item.quantity, 0);
        },
    },

    watch: {
        // Observa mudanças nos filtros globais / Watch global filters changes
        globalFilters: {
            handler() {
                this.loadData();
            },
            deep: true
        },

        // Observa mudanças no estado de loading externo / Watch external loading state
        loading(newVal) {
            if (!newVal) {
                this.loadData();
            }
        }
    },

    mounted() {
        this.loadData(); // carrega dados iniciais
    },

    methods: {
        /**
         * Carrega dados do relatório / Load report data
         */
        async loadData(params) {
            this.loadingData = true;
            this.renderChart = false;

            try {
                const api = this.$store.state.api + "main_items";

                const response = await this.$http.get(api, {params});

                this.dashData = response.data || {graphics: {problemsperiodPerUser: {labels: [], datasets: []}}};

                // Aguarda um pouco antes de renderizar o gráfico
                setTimeout(() => {
                    this.renderChart = true;
                }, 100);

            } catch (error) {
                console.error('Erro ao carregar principais itens:', error);
                const message = error.response.data.errors
                    ? getMessage(error.response.data.errors)
                    : error.response.data || 'Erro ao carregar dados';
                this.$message(null, message, "error");
            } finally {
                this.loadingData = false;
            }
        },

        /**
         * Retorna total de itens / Return total items
         */
        getTotalItems() {
            return this.totalItems;
        },

        /**
         * Retorna total de quantidades / Return total quantities
         */
        getTotalQuantity() {
            return this.totalQuantity;
        },

        /**
         * Retorna média por item / Return average per item
         */
        getAverageQuantity() {
            if (this.totalItems === 0) return 0;
            return Math.round((this.totalQuantity / this.totalItems) * 10) / 10;
        },

        /**
         * Retorna maior quantidade / Return highest quantity
         */
        getLeaderQuantity() {
            if (!this.hasData) return 0;
            return Math.max(...this.itemsList.map(item => item.quantity));
        },

        /**
         * Retorna classe do alerta de status / Return status alert class
         */
        getStatusAlertClass() {
            if (!this.hasData) return 'alert-info';
            if (this.totalQuantity >= 10) return 'alert-success';
            if (this.totalQuantity >= 5) return 'alert-warning';
            return 'alert-info';
        },

        /**
         * Retorna ícone do status / Return status icon
         */
        getStatusIcon() {
            if (!this.hasData) return 'fas fa-info-circle';
            if (this.totalQuantity >= 10) return 'fas fa-check-circle';
            if (this.totalQuantity >= 5) return 'fas fa-exclamation-circle';
            return 'fas fa-info-circle';
        },

        /**
         * Retorna mensagem de status / Return status message
         */
        getStatusMessage() {
            if (!this.hasData) return 'Aguardando Dados';
            if (this.totalQuantity >= 10) return 'Alto Volume de Atividade';
            if (this.totalQuantity >= 5) return 'Volume Moderado de Atividade';
            return 'Baixo Volume de Atividade';
        },

        /**
         * Retorna top itens ordenados / Return top items sorted
         */
        getTopItems() {
            if (!this.hasData) return [];
            return [...this.itemsList].sort((a, b) => b.quantity - a.quantity);
        },

        /**
         * Retorna itens para tabela / Return items for table
         */
        getTableItems() {
            return this.getTopItems();
        },

        /**
         * Calcula percentual / Calculate percentage
         */
        getPercentage(value) {
            if (this.totalQuantity === 0) return 0;
            return Math.round((value / this.totalQuantity) * 100);
        },

        /**
         * Retorna classe de rank do item / Return item rank class
         */
        getItemRankClass(item, index) {
            if (index === 0) return 'first-place';
            if (index === 1) return 'second-place';
            if (index === 2) return 'third-place';
            return 'other-place';
        },

        /**
         * Retorna classe de rank / Return rank class
         */
        getRankClass(index) {
            if (index === 0) return 'gold';
            if (index === 1) return 'silver';
            if (index === 2) return 'bronze';
            return 'regular';
        },

        /**
         * Retorna label de rank / Return rank label
         */
        getRankLabel(index) {
            if (index === 0) return '1º Lugar';
            if (index === 1) return '2º Lugar';
            if (index === 2) return '3º Lugar';
            return `${index + 1}º Lugar`;
        },

        /**
         * Retorna classe da linha da tabela / Return table row class
         */
        getTableRowClass(index) {
            if (index === 0) return 'table-warning';
            return '';
        },

        /**
         * Retorna ícone do item / Return item icon
         */
        getItemIcon(index) {
            if (index === 0) return 'fas fa-crown text-warning';
            if (index === 1) return 'fas fa-medal text-secondary';
            if (index === 2) return 'fas fa-award text-warning';
            return 'fas fa-circle text-muted';
        },

        /**
         * Retorna classe do item / Return item class
         */
        getItemClass(index) {
            if (index === 0) return 'leader-badge';
            if (index === 1) return 'second-badge';
            if (index === 2) return 'third-badge';
            return 'regular-badge';
        },

        /**
         * Retorna status do item / Return item status
         */
        getItemStatus(index) {
            if (index === 0) return 'Líder';
            if (index === 1) return '2º Lugar';
            if (index === 2) return '3º Lugar';
            return 'Regular';
        },

        /**
         * Retorna classe do badge da quantidade / Return quantity badge class
         */
        getQuantityBadgeClass(index) {
            if (index === 0) return 'badge-warning';
            if (index === 1) return 'badge-secondary';
            if (index === 2) return 'badge-info';
            return 'badge-light';
        },

        /**
         * Retorna item líder / Return leader item
         */
        getLeaderItem() {
            if (!this.hasData) return 'N/A';
            const leader = this.getTopItems()[0];
            return leader ? leader.label.substring(0, 20) + (leader.label.length > 20 ? '...' : '') : 'N/A';
        },

        /**
         * Retorna concentração dos top 2 / Return top 2 concentration
         */
        getTopConcentration() {
            if (!this.hasData || this.totalQuantity === 0) return 0;
            const topItems = this.getTopItems().slice(0, 2);
            const topTotal = topItems.reduce((sum, item) => sum + item.quantity, 0);
            return Math.round((topTotal / this.totalQuantity) * 100);
        },

        /**
         * Retorna tipo de distribuição / Return distribution type
         */
        getDistributionType() {
            if (!this.hasData) return 'N/A';
            const leader = this.getTopItems()[0];
            if (!leader) return 'N/A';

            const leaderPercentage = this.getPercentage(leader.quantity);
            if (leaderPercentage >= 50) return 'Concentrada';
            if (leaderPercentage >= 35) return 'Moderada';
            return 'Distribuída';
        },

        /**
         * Retorna insight principal / Return main insight
         */
        getMainInsight() {
            if (!this.hasData) return 'Aguardando dados para análise.';

            const leader = this.getTopItems()[0];
            if (!leader) return 'Nenhum item se destaca significativamente.';

            const percentage = this.getPercentage(leader.quantity);
            return `"${leader.label}" representa ${percentage}% do total, sendo o item mais relevante.`;
        },

        /**
         * Retorna insight secundário / Return secondary insight
         */
        getSecondaryInsight() {
            if (!this.hasData) return 'Continue monitorando os dados.';

            const avgQuantity = this.getAverageQuantity();
            if (avgQuantity >= 3) {
                return 'Há uma boa distribuição de atividade entre os itens analisados.';
            }

            return 'A atividade está concentrada em poucos itens específicos.';
        },
    },
};
</script>

<style scoped>
/* Estilo principal do relatório */
.main-problems-report .card {
    border-radius: 12px;
    overflow: hidden;
}

.bg-gradient-danger {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
}

.text-danger {
    color: #dc3545;
}

.btn-danger {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
    border: none;
    color: white;
}

.btn-danger:hover {
    background: linear-gradient(135deg, #bd2130 0%, #dc3545 100%);
    color: white;
}

/* Alerta de status */
.status-alert {
    margin-bottom: 1.5rem;
}

.alert {
    border: none;
    border-radius: 12px;
    padding: 1.5rem;
}

.alert-content {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.alert-icon {
    font-size: 2rem;
}

.alert-text {
    flex: 1;
}

.alert-details {
    font-size: 0.9rem;
    margin-top: 0.5rem;
    opacity: 0.9;
}

/* Estatísticas críticas */
.critical-stats {
    margin-bottom: 2rem;
}

.critical-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border-left: 4px solid #e3e6f0;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: all 0.3s ease;
    margin-bottom: 1rem;
}

.critical-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.critical-card.primary-stat {
    border-left-color: #FE8D18;
}

.critical-card.quantity-stat {
    border-left-color: #28a745;
}

.critical-card.average-stat {
    border-left-color: #ffc107;
}

.critical-card.leader-stat {
    border-left-color: #dc3545;
}

.critical-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: white;
}

.primary-stat .critical-icon {
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%);
}

.quantity-stat .critical-icon {
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
}

.average-stat .critical-icon {
    background: linear-gradient(135deg, #ffc107 0%, #d39e00 100%);
}

.leader-stat .critical-icon {
    background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%);
}

.critical-value {
    font-size: 1.75rem;
    font-weight: 800;
    color: #2d3748;
    margin-bottom: 0.25rem;
}

.critical-label {
    font-size: 0.875rem;
    color: #718096;
    font-weight: 600;
}

/* Top problemas */
.top-problems {
    margin-bottom: 2rem;
}

.section-title {
    color: #2d3748;
    font-weight: 700;
    margin-bottom: 1rem;
    border-bottom: 2px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.problems-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.problem-item {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border-left: 4px solid #e3e6f0;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: all 0.3s ease;
}

.problem-item:hover {
    transform: translateX(5px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.problem-item.first-place {
    border-left-color: #ffc107;
    background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
}

.problem-item.second-place {
    border-left-color: #6c757d;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.problem-item.third-place {
    border-left-color: #fd7e14;
    background: linear-gradient(135deg, #fff5e6 0%, #ffe0b3 100%);
}

.problem-item.other-place {
    border-left-color: #FE8D18;
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
}

.problem-rank {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.25rem;
}

.first-place .problem-rank {
    background: linear-gradient(135deg, #ffc107 0%, #d39e00 100%);
    color: #212529;
}

.second-place .problem-rank {
    background: linear-gradient(135deg, #6c757d 0%, #545b62 100%);
}

.third-place .problem-rank {
    background: linear-gradient(135deg, #fd7e14 0%, #dc6502 100%);
}

.problem-content {
    flex: 1;
}

.problem-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.problem-title {
    font-weight: 700;
    color: #2d3748;
    font-size: 1.1rem;
}

.severity-badge {
    padding: 0.25rem 0.75rem;
    border-radius: 15px;
    font-size: 0.75rem;
    font-weight: 700;
}

.severity-badge.gold {
    background: #ffc107;
    color: #212529;
}

.severity-badge.silver {
    background: #6c757d;
    color: white;
}

.severity-badge.bronze {
    background: #fd7e14;
    color: white;
}

.severity-badge.regular {
    background: #FE8D18;
    color: white;
}

.problem-stats {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.problem-count {
    color: #718096;
    font-weight: 600;
}

.problem-percentage {
    font-weight: 700;
    color: #FE8D18;
}

.problem-indicator {
    width: 100px;
}

.indicator-bar {
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.indicator-fill {
    height: 100%;
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%);
    transition: width 0.3s ease;
}

/* Container do gráfico */
.chart-container {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e3e6f0;
}

.chart-header {
    margin-bottom: 1rem;
    border-bottom: 1px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.chart-title {
    color: #2d3748;
    font-weight: 700;
    margin-bottom: 0;
}

/* Tabela */
.problems-table {
    background: white;
    border-radius: 12px;
    overflow: hidden;
}

.table {
    margin-bottom: 0;
}

.table thead th {
    border-top: none;
    font-weight: 600;
    color: #5a5c69;
    background-color: #f8f9fc;
}

.table tbody tr:hover {
    background-color: #f8f9fc;
}

.problem-info .problem-name {
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 0.25rem;
}

.problem-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.impact-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.impact-badge.leader-badge {
    background: #fff3cd;
    color: #856404;
}

.impact-badge.second-badge {
    background: #f8f9fa;
    color: #495057;
}

.impact-badge.third-badge {
    background: #ffeaa7;
    color: #8b4513;
}

.impact-badge.regular-badge {
    background: #e3f2fd;
    color: #1976d2;
}

/* Estado vazio */
.empty-state {
    text-align: center;
    padding: 3rem;
}

.empty-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 0.5rem;
}

.empty-subtitle {
    color: #718096;
    font-size: 1.1rem;
}

/* Análise de tendências */
.trend-analysis {
    margin-top: 2rem;
}

.analysis-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e3e6f0;
    height: 100%;
}

.analysis-title {
    color: #2d3748;
    font-weight: 700;
    margin-bottom: 1rem;
    border-bottom: 1px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.analysis-content {
    color: #4a5568;
}

.trend-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f1f3f4;
}

.trend-item:last-child {
    border-bottom: none;
}

.trend-label {
    font-weight: 600;
    color: #4a5568;
}

.trend-value {
    font-weight: 700;
    color: #2d3748;
}

.recommendation-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f1f3f4;
}

.recommendation-item:last-child {
    border-bottom: none;
}

.recommendation-icon {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    flex-shrink: 0;
}

.recommendation-text {
    flex: 1;
    font-weight: 500;
    color: #4a5568;
    line-height: 1.5;
}

/* Badges */
.badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
}

.badge-warning {
    background: linear-gradient(135deg, #ffc107 0%, #d39e00 100%);
    color: #212529;
}

.badge-secondary {
    background: linear-gradient(135deg, #6c757d 0%, #545b62 100%);
}

.badge-info {
    background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
}

.badge-light {
    background: #f8f9fa;
    color: #495057;
}

/* Formulários */
.form-label {
    font-weight: 600;
    color: #5a5c69;
    margin-bottom: 0.5rem;
}

.form-control {
    border-radius: 0.35rem;
    border: 1px solid #d1d3e2;
}

.form-control:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

/* Responsividade */
@media (max-width: 768px) {
    .critical-card {
        flex-direction: column;
        text-align: center;
        gap: 1rem;
    }

    .critical-value {
        font-size: 1.5rem;
    }

    .problem-item {
        flex-direction: column;
        text-align: center;
        gap: 1rem;
    }

    .problem-header {
        flex-direction: column;
        gap: 0.5rem;
    }

    .problem-stats {
        flex-direction: column;
        gap: 0.5rem;
    }

    .trend-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
    }

    .recommendation-item {
        flex-direction: column;
        text-align: center;
        gap: 0.5rem;
    }

    .table-responsive {
        font-size: 0.875rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Indicators/main_items.vue